import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, LoaderIcon, LockIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCheckoutForm, type CheckoutFields, type PaymentMethod } from '../hooks/useCheckoutForm';
import { shippingCost, shippingOptions, FREE_SHIPPING_THRESHOLD } from '../utils/shipping';
import { formatPrice } from '../utils/currency';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { Logo } from '../components/brand/Logo';
const payments: {
  id: PaymentMethod;
  label: string;
}[] = [{
  id: 'ideal',
  label: 'iDEAL'
}, {
  id: 'card',
  label: 'Creditcard'
}, {
  id: 'klarna',
  label: 'Klarna — achteraf betalen'
}, {
  id: 'paypal',
  label: 'PayPal'
}];
export function Checkout() {
  const {
    lines,
    subtotal,
    clear
  } = useCart();
  const form = useCheckoutForm();
  const [status, setStatus] = useState<'idle' | 'processing' | 'done'>('idle');
  const [order, setOrder] = useState<{
    number: string;
    total: number;
  } | null>(null);
  const shipping = shippingCost(subtotal, form.shipping);
  const total = subtotal + shipping;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validate()) return;
    setStatus('processing');
    window.setTimeout(() => {
      setOrder({
        number: `AM-${Math.floor(100000 + Math.random() * 900000)}`,
        total
      });
      clear();
      setStatus('done');
    }, 900);
  };
  return <div className="min-h-screen w-full bg-canvas" data-id="element-405" data-mp="3gghwyfahx6p0">
      <header className="border-b border-line" data-id="element-406" data-mp="3le1a1bvgjdfd">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8" data-id="element-407" data-mp="3qccjvmlr5nxl">
          <Link to="/" aria-label="AMIRA Beauty, naar home" data-id="element-408" data-mp="3qep7oge6ysik"><Logo data-id="element-409" data-mp="3ueziq7guc94a" /></Link>
          <span className="flex items-center gap-2 text-sm text-muted" data-id="element-410" data-mp="3uf99hx9pmkb1">
            <LockIcon className="h-4 w-4" data-id="element-411" data-mp="15l2edv1uv3cb" /> Veilig afrekenen
          </span>
        </div>
      </header>

      {status === 'done' && order ? <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center" data-id="element-412" data-mp="19pjvtnkqt4jx">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-olive text-canvas" data-id="element-413" data-mp="3fohviaejgs5x">
            <CheckIcon className="h-6 w-6" data-id="element-414" data-mp="3aqg9phpqaeqr" />
          </span>
          <h1 className="mt-6 font-display text-4xl uppercase tracking-display" data-id="element-415" data-mp="2mzufat8ncsz8">Bedankt, {form.fields.firstName}!</h1>
          <p className="mt-3 text-lg text-muted" data-id="element-416" data-mp="2izsaib7m7jfq">
            Bestelling <strong className="text-ink" data-id="element-417" data-mp="2wq5lkmfsze9a">{order.number}</strong> ({formatPrice(order.total)}) is ontvangen. Je ontvangt de
            bevestiging op {form.fields.email}.
          </p>
          <p className="mt-6 rounded-2xl bg-surface px-5 py-4 text-sm" data-id="element-418" data-mp="2rxm61jxk291u">Verwachte levering: <strong data-id="element-419" data-mp="2481n311ejbsk">morgen</strong></p>
          <Link to="/" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-420" data-mp="1zb1eaid63uxr">
            Verder winkelen
          </Link>
        </motion.div> : lines.length === 0 ? <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center" data-id="element-421" data-mp="jk8whexly7hk">
          <h1 className="font-display text-3xl uppercase tracking-display" data-id="element-422" data-mp="5t3x8dy5jh7r">Je winkelwagen is leeg</h1>
          <p className="mt-2 text-muted" data-id="element-423" data-mp="arx7zym20u48">Voeg eerst iets toe om af te rekenen.</p>
          <Link to="/collectie/bestsellers" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-424" data-mp="yggi8jonzgyy">
            Shop bestsellers
          </Link>
        </div> : <form onSubmit={submit} noValidate className="mx-auto grid max-w-6xl gap-10 px-5 py-10 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-14" data-id="element-425" data-mp="oiafy0yy48tk">
          <div className="space-y-10 lg:col-span-7" data-id="element-426" data-mp="399168jkkuj4i">
            <h1 className="font-display text-3xl uppercase tracking-display sm:text-4xl" data-id="element-427" data-mp="3976mh8l1kx20">Afrekenen</h1>

            <fieldset data-id="element-428" data-mp="3e95nwcx81jwb">
              <legend className="text-lg font-semibold" data-id="element-429" data-mp="3e5hwbjbc77k8">Contact</legend>
              <div className="mt-4" data-id="element-430" data-mp="3tisky1v2q6jd">
                <Field id="email" label="E-mailadres" type="email" autoComplete="email" form={form} data-id="element-431" data-mp="3or144j1pzf6f" />
              </div>
            </fieldset>

            <fieldset data-id="element-432" data-mp="3kpz4i13w513x">
              <legend className="text-lg font-semibold" data-id="element-433" data-mp="3kn34864km0cb">Bezorgadres</legend>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-434" data-mp="3frdyfl1yjn7k">
                <Field id="firstName" label="Voornaam" autoComplete="given-name" form={form} data-id="element-435" data-mp="gbhfjxbzukxp" />
                <Field id="lastName" label="Achternaam" autoComplete="family-name" form={form} data-id="element-436" data-mp="c9njk2cqu53v" />
                <div className="sm:col-span-2" data-id="element-437" data-mp="7a13zm9g0crw">
                  <Field id="address" label="Straat en huisnummer" autoComplete="street-address" form={form} data-id="element-438" data-mp="78q0lnjwt71b" />
                </div>
                <Field id="postcode" label="Postcode" autoComplete="postal-code" form={form} data-id="element-439" data-mp="2dkikztmocii" />
                <Field id="city" label="Plaats" autoComplete="address-level2" form={form} data-id="element-440" data-mp="2ry5nw8jx6dm7" />
              </div>
            </fieldset>

            <fieldset data-id="element-441" data-mp="2cy9vqro2z8o3">
              <legend className="text-lg font-semibold" data-id="element-442" data-mp="2cytcw65wpt12">Verzending</legend>
              <div className="mt-4 grid gap-2" data-id="element-443" data-mp="2hvs7sqxea40b">
                {shippingOptions.map(o => {
              const price = shippingCost(subtotal, o.id);
              return <Option key={o.id} name="shipping" checked={form.shipping === o.id} onChange={() => form.setShipping(o.id)} data-id="element-444" data-mp="2rm4sqk94fz6z">
                      <span className="flex-1" data-id="element-445" data-mp="2vn6qsxray57d">
                        <span className="block font-medium" data-id="element-446" data-mp="30n2vkz7tq0nz">{o.label}</span>
                        <span className="block text-sm text-muted" data-id="element-447" data-mp="30l8bto8agelh">{o.detail}</span>
                      </span>
                      <span className="text-sm font-medium" data-id="element-448" data-mp="35kbfp5cj8erv">{price === 0 ? 'Gratis' : formatPrice(price)}</span>
                    </Option>;
            })}
                {subtotal < FREE_SHIPPING_THRESHOLD && <p className="text-sm text-muted" data-id="element-449" data-mp="1g6pu7c19kz9r">Nog {formatPrice(FREE_SHIPPING_THRESHOLD - subtotal)} tot gratis standaard verzending.</p>}
              </div>
            </fieldset>

            <fieldset data-id="element-450" data-mp="137yvmuugsw8q">
              <legend className="text-lg font-semibold" data-id="element-451" data-mp="138qo3wsrmdus">Betaalmethode</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-452" data-mp="1i4zlest3ih4k">
                {payments.map(p => <Option key={p.id} name="payment" checked={form.payment === p.id} onChange={() => form.setPayment(p.id)} data-id="element-453" data-mp="1d5d5ucpsoosl">
                    <span className="font-medium" data-id="element-454" data-mp="1r3tg2dmv8xfh">{p.label}</span>
                  </Option>)}
              </div>
            </fieldset>

            <div className="lg:hidden" data-id="element-455" data-mp="20uxsp6p2k675">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-456" data-mp="20tw8abm5y7cr" />
            </div>

            <button type="submit" disabled={status === 'processing'} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry disabled:opacity-70" data-id="element-457" data-mp="1vwmk0zaig1qi">
              {status === 'processing' ? <>
                  <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-458" data-mp="2thxfk1lf9fty" /> Bezig met betalen…
                </> : <>Betaal {formatPrice(total)}</>}
            </button>
            <p className="-mt-6 text-center text-xs text-muted" data-id="element-459" data-mp="2kfy1ni5perv8">30 dagen bedenktijd · Veilig betalen via SSL</p>
          </div>

          <div className="hidden lg:col-span-5 lg:block" data-id="element-460" data-mp="25qs561y93fr1">
            <div className="sticky top-8" data-id="element-461" data-mp="2aj31kuts8jeu">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-462" data-mp="2ak4ihfxhc04h" />
            </div>
          </div>
        </form>}
    </div>;
}
type FieldProps = {
  id: keyof CheckoutFields;
  label: string;
  type?: string;
  autoComplete?: string;
  form: ReturnType<typeof useCheckoutForm>;
};
function Field({
  id,
  label,
  type = 'text',
  autoComplete,
  form
}: FieldProps) {
  const error = form.errors[id];
  return <div data-id="element-463" data-mp="27acw74hfzqnd">
      <label htmlFor={`co-${id}`} className="text-sm font-medium" data-id="element-464" data-mp="27a35feokpfgm">{label}</label>
      <input id={`co-${id}`} type={type} autoComplete={autoComplete} value={form.fields[id]} onChange={e => form.update(id, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `co-${id}-err` : undefined} className={`mt-1.5 h-12 w-full rounded-xl border bg-white px-4 text-ink focus:outline-none ${error ? 'border-pink focus:border-pink' : 'border-line focus:border-ink'}`} data-id="element-465" data-mp="277z1iwqocggc" />
      {error && <p id={`co-${id}-err`} className="mt-1.5 text-sm text-berry" data-id="element-466" data-mp="1jixpz0mjop6u">{error}</p>}
    </div>;
}
function Option({
  name,
  checked,
  onChange,
  children
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return <label className={`flex cursor-pointer items-center gap-4 rounded-xl border bg-white px-4 py-4 transition-colors duration-150 ${checked ? 'border-ink ring-1 ring-ink' : 'border-line hover:border-muted'}`} data-id="element-467" data-mp="15jy39yilekdb">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="h-4 w-4 accent-[#1C1714]" data-id="element-468" data-mp="3cbcgwrg9n61f" />
      {children}
    </label>;
}